import React, { useState, useEffect } from 'react';
import { BarChart3, TrendingUp, iser, User, Users, Gamepad2 } from 'lucide-react';

export default function Analytics() {
  const [dateRange, setDateRange] = useState('30days');
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    newHosts: 1,
    totalGames: 0,
    avgGamesPerHost: 0,
    revenueGrowth: 0
  });

  useEffect(() => {
    fetchAnalytics();
  }, [dateRange]);

  const fetchAnalytics = async () => {
    try {
      // TODO: Replace with actual API call
      // const response = await analyticsAPI.getOverview({ range: dateRange });
      // setStats(response.data);
      
      setTimeout(() => {
        setStats({
          newHosts: 1,
          totalGames: 0,
          avgGamesPerHost: 0,
          revenueGrowth: 0
        });
        setLoading(false);
      }, 500);
    } catch (error) {
      console.error('Error fetching analytics:', error);
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-xl text-gray-600">Loading analytics...</div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-3xl font-bold text-gray-800">Analytics</h1>
        <select
          value={dateRange}
          onChange={(e) => setDateRange(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-500"
        >
          <option value="7days">Last 7 Days</option>
          <option value="30days">Last 30 Days</option>
          <option value="90days">Last 90 Days</option>
          <option value="1year">Last Year</option>
        </select>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="stat-card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-600 uppercase">New Hosts</h3>
            <User size={32} className="text-slate-600" />
          </div>
          <p className="text-4xl font-bold text-slate-600">{stats.newHosts}</p>
          <p className="text-sm text-green-600 mt-2">+100% from last period</p>
        </div>

        <div className="stat-card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-600 uppercase">Total Games</h3>
            <Gamepad2 size={32} className="text-slate-600" />
          </div>
          <p className="text-4xl font-bold text-azure-600">{stats.totalGames}</p>
          <p className="text-sm text-gray-500 mt-2">This period</p>
        </div>

        <div className="stat-card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-600 uppercase">Avg Games/Host</h3>
            <BarChart3 size={32} className="text-slate-600" />
          </div>
          <p className="text-4xl font-bold text-orange-600">{stats.avgGamesPerHost}</p>
          <p className="text-sm text-gray-500 mt-2">Per host</p>
        </div>

        <div className="stat-card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-600 uppercase">Revenue Growth</h3>
            <TrendingUp size={32} className="text-slate-600" />
          </div>
          <p className="text-4xl font-bold text-green-600">{stats.revenueGrowth}%</p>
          <p className="text-sm text-gray-500 mt-2">Month over month</p>
        </div>
      </div>

      {/* Charts Placeholders */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Revenue Chart */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <h2 className="text-xl font-bold text-gray-800 mb-4">Revenue Over Time</h2>
          <div className="bg-gradient-to-br from-slate-50 to-pink-50 rounded-lg p-12 text-center">
            <div className="mb-4"><TrendingUp size={64} className="text-slate-400" /></div>
            <p className="text-gray-600">Chart Coming Soon</p>
            <p className="text-sm text-gray-500 mt-2">Revenue trends visualization</p>
          </div>
        </div>

        {/* Games Chart */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <h2 className="text-xl font-bold text-gray-800 mb-4">Games Played</h2>
          <div className="bg-gradient-to-br from-azure-50 to-blue-50 rounded-lg p-12 text-center">
            <Gamepad2 size={32} className="text-slate-600" />
            <p className="text-gray-600">Chart Coming Soon</p>
            <p className="text-sm text-gray-500 mt-2">Game activity over time</p>
          </div>
        </div>
      </div>

      {/* Host Growth Chart */}
      <div className="bg-white rounded-xl shadow-md p-6 mb-8">
        <h2 className="text-xl font-bold text-gray-800 mb-4">Host Growth</h2>
        <div className="bg-gradient-to-br from-orange-50 to-yellow-50 rounded-lg p-12 text-center">
          <div className="mb-4"><Users size={64} className="text-slate-400" /></div>
          <p className="text-gray-600">Chart Coming Soon</p>
          <p className="text-sm text-gray-500 mt-2">New host signups over time</p>
        </div>
      </div>

      {/* Template Usage */}
      <div className="bg-white rounded-xl shadow-md p-6">
        <h2 className="text-xl font-bold text-gray-800 mb-4">Popular Templates</h2>
        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div>
              <p className="font-semibold text-gray-800">General Knowledge - Round 1</p>
              <p className="text-sm text-gray-600">Official template</p>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-slate-600">0</p>
              <p className="text-sm text-gray-500">times used</p>
            </div>
          </div>

          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div>
              <p className="font-semibold text-gray-800">Sports & Entertainment</p>
              <p className="text-sm text-gray-600">Official template</p>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-slate-600">0</p>
              <p className="text-sm text-gray-500">times used</p>
            </div>
          </div>

          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div>
              <p className="font-semibold text-gray-800">Music Through the Decades</p>
              <p className="text-sm text-gray-600">Official template</p>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-slate-600">0</p>
              <p className="text-sm text-gray-500">times used</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
